// apps/spa/src/hooks/useFormStatePersistence.ts
// Save in-flight form state to sessionStorage BEFORE the SessionExpiredModal's
// 'Log in again' redirect, then auto-hydrate it after the OAuth round-trip
// returns the officer to the same route.
//
// Key shape: `guardian:form-state:<route>:<formId>` — route+formId scoped so a
// reminder editor's draft never collides with a slash-command editor's draft.
// Single-use: hydration removes the key so a refresh after OAuth doesn't
// re-apply stale values.
//
// Caller pattern:
//   const persist = useFormStatePersistence({
//     formId: 'reminder-new',
//     route: '/reminders/new',
//     getFormValues: () => form.getValues(),
//     setFormValues: (v) => form.reset(v),
//   });
//   // Before redirect (e.g. from SessionExpiredModal "Log in again"):
//   persist();

import { useEffect, useRef } from 'react';
import { _FORM_STATE_PREFIX } from './useSessionExpired';

export interface UseFormStatePersistenceArgs<T> {
  formId: string;
  route: string;
  getFormValues: () => T;
  setFormValues: (values: T) => void;
}

/**
 * Returns a `persist` function that callers invoke before navigating away.
 * On mount, hydrates from sessionStorage if the route+formId matches.
 *
 * The hydrate step is single-use — after applying, the key is removed.
 */
export function useFormStatePersistence<T>(
  args: UseFormStatePersistenceArgs<T>,
): () => void {
  const argsRef = useRef(args);
  argsRef.current = args;

  const storageKey = `${_FORM_STATE_PREFIX}${args.route}:${args.formId}`;

  // Hydrate on mount if a saved value exists for this route+formId.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as T;
      argsRef.current.setFormValues(parsed);
      sessionStorage.removeItem(storageKey);
    } catch {
      // Corrupt entry — drop it so we don't loop on every mount.
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
    }
    // Intentionally only on mount — we don't want re-runs to clobber edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return a stable persist callback. Reads the latest values via ref so the
  // caller doesn't need to memoise getFormValues.
  function persist(): void {
    if (typeof window === 'undefined') return;
    try {
      const values = argsRef.current.getFormValues();
      sessionStorage.setItem(storageKey, JSON.stringify(values));
    } catch {
      /* sessionStorage may be disabled — fail silently per RESEARCH guidance */
    }
  }

  return persist;
}
